import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle2, ShieldAlert, Clock, X, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CURRENCIES = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
];

const PLAN_META = {
  growth: {
    name: "Growth",
    tagline: "For founders ready to scale.",
    outcomes: [
      "Everything in Starter",
      "Board · Leadership Team advisory",
      "Business Data — custom trackers",
      "Priority support",
    ],
  },
  business: {
    name: "Business",
    tagline: "For teams operating at pace.",
    outcomes: [
      "Everything in Growth",
      "Team roles, members & permissions",
      "Shared business context across the team",
      "Advanced controls & reporting",
    ],
  },
};

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function BillingSettings() {
  const [params] = useSearchParams();
  const initialPlan = params.get("plan") === "business" ? "business" : "growth";
  const initialBilling = params.get("billing") === "annual" ? "annual" : "monthly";
  const [plan, setPlan] = useState(initialPlan);
  const [billing, setBilling] = useState(initialBilling);
  const [currency, setCurrency] = useState("INR");
  const [status, setStatus] = useState(null); // razorpay status
  const [busy, setBusy] = useState(false);
  const [entitlements, setEntitlements] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const [{ data: st }, meRes] = await Promise.all([
        api.get("/billing/razorpay/status"),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setStatus(st);
      if (meRes.data?.org) setEntitlements(meRes.data.org.entitlements || null);
      try {
        const subsRes = await api.get("/billing/subscriptions");
        setSubscriptions(subsRes.data.subscriptions || []);
      } catch { /* endpoint may not exist yet — non-fatal */ }
    } catch (err) {
      console.warn("billing status load failed", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const price = useMemo(() => {
    if (!status?.pricing) return null;
    const row = status.pricing[currency]?.[plan];
    if (!row) return null;
    return billing === "annual" ? row.annual : row.monthly;
  }, [status, currency, plan, billing]);

  const startCheckout = async () => {
    if (!status?.configured) {
      toast.error("Payments are not enabled on this environment yet. Please try again shortly or contact support.");
      return;
    }
    setBusy(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load Razorpay checkout. Check your connection.");
      const { data } = await api.post("/billing/razorpay/checkout", { plan, billing, currency });
      const rz = new window.Razorpay({
        key: data.key_id,
        order_id: data.order_id,
        amount: data.amount,
        currency: data.currency,
        name: "Stupeco WorkSpace",
        description: `${PLAN_META[plan].name} · ${billing === "annual" ? "Annual" : "Monthly"}`,
        prefill: { email: user?.email, name: user?.name },
        theme: { color: "#0D9488" },
        handler: async (resp) => {
          try {
            await api.post("/billing/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment authenticated. Activating your plan…");
            // Webhook activates entitlement; poll /auth/me briefly
            let attempts = 0;
            const poll = async () => {
              attempts += 1;
              try {
                const me = await api.get("/auth/me");
                const active = me.data?.org?.entitlements?.plan;
                if (active === plan) {
                  toast.success(`${PLAN_META[plan].name} plan activated 🎉`);
                  await load();
                  return;
                }
              } catch { /* noop */ }
              if (attempts < 6) setTimeout(poll, 2500);
              else {
                toast.info("Payment received. Your plan will activate shortly — you can refresh in a moment.");
                await load();
              }
            };
            poll();
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Payment verification failed. If money was debited we'll reconcile it.");
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Checkout cancelled — no changes made.");
          },
        },
      });
      rz.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || String(err));
    } finally {
      setBusy(false);
    }
  };

  const currentPlan = entitlements?.plan || "starter";
  const isFree = currentPlan === "starter" || currentPlan === "free";

  return (
    <div data-testid="billing-settings">
      <Seo title="Billing" description="Manage your Stupeco WorkSpace subscription." path="/app/settings/billing" />

      {/* Current plan */}
      <section className="mb-6 rounded-2xl border border-border bg-card p-6">
        <p className="eyebrow text-brand-teal">Current plan</p>
        <div className="mt-2 flex flex-wrap items-baseline gap-2">
          <h2 className="font-display text-2xl font-semibold capitalize">{currentPlan}</h2>
          {entitlements?.source && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {entitlements.source}
            </span>
          )}
          {entitlements?.expires_at && (
            <span className="text-xs text-muted-foreground">until {entitlements.expires_at.slice(0, 10)}</span>
          )}
        </div>
        {isFree && <p className="mt-2 text-sm text-muted-foreground">You're on the free Starter plan. Upgrade to unlock the Board, Business Data and priority support.</p>}
        {!isFree && <p className="mt-2 text-sm text-muted-foreground">Manage or change your plan below. Downgrades take effect at the end of your current billing cycle.</p>}
      </section>

      {/* Upgrade flow */}
      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="eyebrow text-brand-teal">Upgrade</p>
            <h3 className="mt-1 font-display text-xl font-semibold tracking-tight">Choose the WorkSpace that fits your business.</h3>
          </div>
          {status && !status.configured && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 text-[11px] font-semibold text-amber-500" data-testid="billing-not-configured">
              <ShieldAlert className="h-3.5 w-3.5" /> Payments not yet enabled
            </span>
          )}
        </div>

        {/* Plan selector */}
        <div className="mt-5 grid gap-4 md:grid-cols-2" data-testid="plan-selector">
          {["growth", "business"].map((p) => (
            <button
              key={p}
              onClick={() => setPlan(p)}
              data-testid={`plan-option-${p}`}
              className={`rounded-2xl border p-5 text-left transition-colors ${plan === p ? "border-brand-teal/60 bg-brand-teal/5" : "border-border hover:border-foreground/20"}`}
            >
              <div className="flex items-center justify-between">
                <p className="font-display text-lg font-semibold">{PLAN_META[p].name}</p>
                {plan === p && <CheckCircle2 className="h-4 w-4 text-brand-teal" />}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{PLAN_META[p].tagline}</p>
              <ul className="mt-3 space-y-1 text-[13px] text-muted-foreground">
                {PLAN_META[p].outcomes.map((o) => <li key={o} className="flex items-start gap-1.5"><span className="mt-2 h-1 w-1 rounded-full bg-brand-teal" /> {o}</li>)}
              </ul>
            </button>
          ))}
        </div>

        {/* Billing cycle + currency */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="billing-cycle-selector">
          <div className="inline-flex items-center gap-1 rounded-full border border-border bg-background p-1">
            <button onClick={() => setBilling("monthly")} data-testid="billing-cycle-monthly" className={`rounded-full px-3 py-1.5 text-xs font-semibold ${billing === "monthly" ? "bg-foreground text-background" : "text-muted-foreground"}`}>Monthly</button>
            <button onClick={() => setBilling("annual")} data-testid="billing-cycle-annual" className={`rounded-full px-3 py-1.5 text-xs font-semibold ${billing === "annual" ? "bg-foreground text-background" : "text-muted-foreground"}`}>Annual · save 2 months</button>
          </div>
          <div className="inline-flex items-center gap-1 rounded-full border border-border bg-background p-1">
            {CURRENCIES.map((c) => (
              <button key={c.code} onClick={() => setCurrency(c.code)} data-testid={`billing-currency-${c.code}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${currency === c.code ? "bg-foreground text-background" : "text-muted-foreground"}`}>{c.symbol} {c.code}</button>
            ))}
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-border bg-background/50 p-4">
          <div className="flex items-baseline gap-2">
            <p className="font-display text-3xl font-semibold" data-testid="billing-price">
              {status?.pricing ? `${CURRENCIES.find((c) => c.code === currency)?.symbol}${price?.toLocaleString?.() || price}` : "…"}
            </p>
            <span className="text-sm text-muted-foreground">{billing === "annual" ? "/ year" : "/ month"}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {billing === "annual" ? "Billed once per year — equivalent to 10 months at the monthly rate." : "Billed monthly. Cancel any time."}
          </p>
        </div>

        <button
          onClick={startCheckout}
          disabled={busy || !status?.configured}
          data-testid="billing-checkout-btn"
          className="mt-5 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening checkout…</> : <><Sparkles className="h-4 w-4" /> Upgrade to {PLAN_META[plan].name}</>}
        </button>
        {status && !status.configured && (
          <p className="mt-3 text-xs text-muted-foreground">
            Payments aren't enabled on this environment yet. If you're an early user with a complimentary plan, your access continues unchanged.
          </p>
        )}
      </section>

      {/* Recent subscription activity */}
      {subscriptions.length > 0 && (
        <section className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="billing-history">
          <p className="eyebrow text-brand-teal">Recent activity</p>
          <ul className="mt-3 divide-y divide-border">
            {subscriptions.slice(0, 6).map((s) => (
              <li key={s.subscription_id} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <p className="text-sm font-medium capitalize">{s.plan} · {s.billing}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.status === "active" && <><CheckCircle2 className="mr-1 inline h-3 w-3 text-brand-teal" /> Active until {s.expires_at?.slice(0,10)}</>}
                    {s.status === "pending" && <><Clock className="mr-1 inline h-3 w-3 text-amber-500" /> Pending payment</>}
                    {s.status === "past_due" && <><X className="mr-1 inline h-3 w-3 text-destructive" /> Payment failed</>}
                    {s.status === "authenticated" && <><Clock className="mr-1 inline h-3 w-3 text-amber-500" /> Awaiting confirmation</>}
                  </p>
                </div>
                <span className="text-sm text-muted-foreground">{s.currency} {s.amount}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="mt-6 text-xs text-muted-foreground">
        By subscribing you agree to the <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>. Powered by Razorpay Secure Checkout <ExternalLink className="inline h-3 w-3" />.
      </p>
    </div>
  );
}
